import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import QRCode from "qrcode";
import "./booklet.css";
import { defaultDoc, makeBlock, makeId, BLOCK_LABELS, migrateDoc, groupIntoPages, FONT_OPTIONS, DEFAULT_BRAND } from "./defaultDoc";
import { USER_GUIDE_DOC } from "./userGuide";
import {
  Printer, FileDown, FileUp, Pencil, RotateCcw, ImagePlus, Upload, Trash2,
  Lightbulb, AlertTriangle, Info, Clock, CheckCircle2, ShieldAlert, StickyNote,
  ArrowUp, ArrowDown, Copy, Plus, X, BookOpen, FileText, Maximize2, RefreshCcw,
  Scissors, Undo2, Redo2, QrCode, Settings, FolderOpen, Save, Download, Loader2,
  Lock, LogOut, HelpCircle,
} from "lucide-react";

/* A block's rendered height inside the continuous editor includes editing
   chrome (toolbars etc.) that won't be present on the printed page, so
   automatic page breaks land a little conservatively — pages may end very
   slightly short rather than ever overflowing. */
const MM_TO_PX = 96 / 25.4;
const PAGE_HEIGHT_MM = 210;
const MARGIN_MM = 10;
const FOOTER_RESERVE_MM = 16;
const HEADER_RESERVE_MM = 14;
const BLOCK_GAP_MM = 4;
const AVAILABLE_PAGE_PX = (PAGE_HEIGHT_MM - MARGIN_MM - FOOTER_RESERVE_MM - HEADER_RESERVE_MM) * MM_TO_PX;
const BLOCK_GAP_PX = BLOCK_GAP_MM * MM_TO_PX;

/* Insert *soft* overflow-warning markers into a flat block list, alongside
   any manual pagebreak markers already in it. These are pure render-time
   hints shown only in the editor — pagination for Done/print/booklet is
   driven solely by the manual pagebreak blocks actually stored in the
   document, never by these. `beforeId` lets a warning be "promoted" into
   a real manual break at exactly that spot. */
function withAutoBreaks(blocks, autoBreakIds) {
  const out = [];
  for (const b of blocks) {
    if (b.type === "pagebreak") { out.push(b); continue; }
    if (autoBreakIds.has(b.id)) out.push({ id: `auto-${b.id}`, type: "pagebreak", tag: "", auto: true, beforeId: b.id });
    out.push(b);
  }
  return out;
}

function sameIdSet(a, b) {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

/* ================= primitives ================= */
function EditableText({ value, onCommit, tag = "div", className = "", placeholder = "", editing, style }) {
  const ref = useRef(null);
  /* Re-sync the visible text whenever `value` changes from outside (e.g.
     undo/redo, or another control writing to the same field) — but only
     while the field isn't focused, so a user's in-progress typing is never
     clobbered by a state update triggered elsewhere. */
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.innerText = value || "";
    }
  }, [value]);
  const Tag = tag;
  return (
    <Tag
      ref={ref}
      className={`tn-editable ${className}`}
      data-ph={placeholder}
      style={style}
      contentEditable={editing}
      suppressContentEditableWarning
      onBlur={(e) => onCommit(e.currentTarget.innerText)}
    />
  );
}

/* Image slot with upload + physical resize (scale) + zoom/pan crop controls.
   `scale` changes the slot's footprint on the page (physical size %).
   `zoom` + `panX`/`panY` crop/reposition the photo inside a fixed frame. */
function Slot({ value, onChange, code, label, hint, variant = "", style,
  scale = 100, zoom = 100, panX = 0, panY = 0,
  onScaleChange, onZoomChange, onPanChange }) {
  const inputRef = useRef(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };

  const outerStyle = (() => {
    if (scale === 100) return style;
    if (style && (typeof style.height === "string" || typeof style.width === "string")) {
      const s = { ...style };
      ["height", "width", "maxWidth", "maxHeight"].forEach((k) => {
        if (typeof s[k] === "string" && s[k].endsWith("mm")) {
          s[k] = `${(parseFloat(s[k]) * scale / 100).toFixed(2)}mm`;
        }
      });
      return s;
    }
    return { ...style, width: `${scale}%`, margin: "0 auto" };
  })();

  const imgStyle = (zoom !== 100 || panX !== 0 || panY !== 0)
    ? { transform: `scale(${zoom / 100}) translate(${panX}%, ${panY}%)`, transformOrigin: "center" }
    : undefined;

  return (
    <div className={`slot-wrap ${variant} ${adjustOpen ? "slot-wrap--adjusting" : ""}`} style={outerStyle}>
      <div className={`slot ${value ? "slot--filled" : ""}`}>
        <span className="slot__code">{code}</span>
        {value ? <img src={value} alt={label} style={imgStyle} /> : (
          <div className="slot__ph"><ImagePlus /><b>{label}</b>{hint && <span>{hint}</span>}</div>
        )}
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
        <div className="slot__tools no-print">
          <div className="slot__tools-row">
            <button className="slot__btn" onClick={() => inputRef.current?.click()}><Upload /> {value ? "Replace" : "Add image"}</button>
            {value && <button className="slot__btn slot__btn--danger" onClick={() => onChange(null)}><Trash2 /> Remove</button>}
            {value && onScaleChange && <button className="slot__btn" onClick={() => setAdjustOpen((v) => !v)}><Maximize2 /> Resize</button>}
          </div>
        </div>
      </div>
      {value && adjustOpen && onScaleChange && (
        <div className="slot__adjust no-print" onClick={(e) => e.stopPropagation()}>
          <label>Size <input type="range" min="50" max="150" value={scale} onChange={(e) => onScaleChange(Number(e.target.value))} /><span>{scale}%</span></label>
          <label>Zoom <input type="range" min="100" max="250" value={zoom} onChange={(e) => onZoomChange(Number(e.target.value))} /><span>{zoom}%</span></label>
          <label>Pan X <input type="range" min="-50" max="50" value={panX} onChange={(e) => onPanChange({ panX: Number(e.target.value), panY })} /></label>
          <label>Pan Y <input type="range" min="-50" max="50" value={panY} onChange={(e) => onPanChange({ panX, panY: Number(e.target.value) })} /></label>
          <button className="slot__btn" onClick={() => { onScaleChange(100); onZoomChange(100); onPanChange({ panX: 0, panY: 0 }); }}><RefreshCcw /> Reset</button>
        </div>
      )}
    </div>
  );
}

/* Renders a QR code as a data-URL <img> generated client-side (no network
   request at render/print time), regenerated whenever the target URL
   changes. */
function QrImage({ url, sizeMm = 28 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url || " ", { margin: 1, width: 256 })
      .then((d) => { if (!cancelled) setSrc(d); })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
  }, [url]);
  return (
    <div className="qr__img" style={{ width: `${sizeMm}mm`, height: `${sizeMm}mm` }}>
      {src ? <img src={src} alt="QR code" /> : <QrCode className="qr__placeholder" />}
    </div>
  );
}

const CalloutIcon = ({ t }) => (
  t === "warn" ? <AlertTriangle /> :
  t === "info" ? <Info /> :
  t === "danger" ? <ShieldAlert /> :
  t === "note" ? <StickyNote /> :
  <Lightbulb />
);

/* ================= block renderer ================= */
function Block({ block, editing, ctx }) {
  const b = block;
  const logoInput = useRef(null);
  const set = (patch) => ctx.mutate(b.id, (x) => ({ ...x, ...(typeof patch === "function" ? patch(x) : patch) }));
  const field = (key, ph) => (v) => set({ [key]: v });

  let body = null;
  switch (b.type) {
    case "hero":
      body = (
        <div className="hero">
          <div className="hero__row">
            <div className="hero__logo">
              <img src={ctx.coverLogoSrc} alt={`${ctx.brandName} logo`} />
              <input ref={logoInput} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => ctx.setLogo(r.result); r.readAsDataURL(f); e.target.value = ""; }} />
              {editing && (
                <div className="hero__logo-tools no-print">
                  <button className="slot__btn" onClick={() => logoInput.current?.click()}><Upload /> Upload logo</button>
                  {ctx.hasCustomLogo && <button className="slot__btn slot__btn--danger" onClick={() => ctx.setLogo(null)}><Trash2 /> Default</button>}
                </div>
              )}
            </div>
            <div className="hero__titles">
              <EditableText tag="div" className="hero__code" editing={editing} value={b.model} onCommit={field("model")} placeholder="Model / rev" />
              <EditableText tag="h1" className="hero__title" editing={editing} value={b.title} onCommit={field("title")} placeholder="Title" />
              <EditableText tag="p" className="hero__sub" editing={editing} value={b.subtitle} onCommit={field("subtitle")} placeholder="Subtitle" />
            </div>
          </div>
        </div>
      );
      break;
    case "section":
      body = (
        <div>
          <EditableText tag="p" className="eyebrow" editing={editing} value={b.eyebrow} onCommit={field("eyebrow")} placeholder="EYEBROW" />
          <h2 className="sec-title">
            {b.stageNum ? <span className="n">{b.stageNum}</span> : null}
            <EditableText tag="span" editing={editing} value={b.title} onCommit={field("title")} placeholder="Section title" />
          </h2>
          {editing && (
            <div className="block-opts">
              <label>Stage badge <input value={b.stageNum} placeholder="(none)" style={{ width: 60 }} onChange={(e) => set({ stageNum: e.target.value })} /></label>
            </div>
          )}
        </div>
      );
      break;
    case "paragraph":
      body = (
        <div>
          <EditableText tag="p" className={`para ${b.variant === "lede" ? "para--lede" : b.variant === "panel" ? "para--panel" : ""}`} editing={editing} value={b.text} onCommit={field("text")} placeholder="Paragraph text" />
          {editing && (
            <div className="block-opts">
              <label>Style
                <select value={b.variant} onChange={(e) => set({ variant: e.target.value })}>
                  <option value="body">Body</option>
                  <option value="lede">Lede (intro)</option>
                  <option value="panel">Amber panel</option>
                </select>
              </label>
            </div>
          )}
        </div>
      );
      break;
    case "step": {
      const hasImg = b.image?.enabled;
      const cls = !hasImg ? "step--noimg" : b.image.side === "left" ? "step--left" : "";
      const bodyCol = (
        <div className="step__body">
          <div className="step__num">
            <b><EditableText tag="span" editing={editing} value={b.num} onCommit={field("num")} placeholder="#" /></b>
            <h4><EditableText tag="span" editing={editing} value={b.title} onCommit={field("title")} placeholder="Step title" /></h4>
          </div>
          <EditableText tag="p" className="step__text" editing={editing} value={b.text} onCommit={field("text")} placeholder="Step description" />
          {b.callout?.enabled && (
            <div className={`callout callout--${b.callout.ctype}`}>
              <CalloutIcon t={b.callout.ctype} />
              <div>
                <b>{b.callout.label} </b>
                <EditableText tag="span" editing={editing} value={b.callout.text} onCommit={(v) => set((x) => ({ callout: { ...x.callout, text: v } }))} placeholder="Callout text" />
              </div>
            </div>
          )}
        </div>
      );
      const mediaCol = hasImg ? (
        <div className="step__media">
          <Slot
            value={b.image.src} code={b.image.code} label={b.image.label}
            variant={b.image.aspect === "16:9" ? "slot--wide" : ""}
            scale={b.image.scale ?? 100} zoom={b.image.zoom ?? 100} panX={b.image.panX ?? 0} panY={b.image.panY ?? 0}
            onChange={(src) => set((x) => ({ image: { ...x.image, src } }))}
            onScaleChange={(scale) => set((x) => ({ image: { ...x.image, scale } }))}
            onZoomChange={(zoom) => set((x) => ({ image: { ...x.image, zoom } }))}
            onPanChange={({ panX, panY }) => set((x) => ({ image: { ...x.image, panX, panY } }))}
          />
        </div>
      ) : null;
      body = (
        <div>
          <div className={`step ${cls}`}>{bodyCol}{mediaCol}</div>
          {editing && (
            <div className="block-opts">
              <label><input type="checkbox" checked={hasImg} onChange={(e) => set((x) => ({ image: { ...x.image, enabled: e.target.checked } }))} /> Image</label>
              {hasImg && <label>Side
                <select value={b.image.side} onChange={(e) => set((x) => ({ image: { ...x.image, side: e.target.value } }))}>
                  <option value="right">Right</option><option value="left">Left</option>
                </select></label>}
              {hasImg && <label>Aspect
                <select value={b.image.aspect || "4:3"} onChange={(e) => set((x) => ({ image: { ...x.image, aspect: e.target.value } }))}>
                  <option value="4:3">4:3</option><option value="16:9">16:9</option>
                </select></label>}
              {hasImg && <label>Code <input value={b.image.code} style={{ width: 54 }} onChange={(e) => set((x) => ({ image: { ...x.image, code: e.target.value } }))} /></label>}
              <label><input type="checkbox" checked={b.callout?.enabled} onChange={(e) => set((x) => ({ callout: { ...x.callout, enabled: e.target.checked } }))} /> Callout</label>
              {b.callout?.enabled && <label>Type
                <select value={b.callout.ctype} onChange={(e) => set((x) => ({ callout: { ...x.callout, ctype: e.target.value } }))}>
                  <option value="tip">Tip</option><option value="info">Info</option><option value="warn">Warning</option><option value="danger">Danger</option><option value="note">Note</option>
                </select></label>}
              {b.callout?.enabled && <label>Label <input value={b.callout.label} style={{ width: 70 }} onChange={(e) => set((x) => ({ callout: { ...x.callout, label: e.target.value } }))} /></label>}
            </div>
          )}
        </div>
      );
      break;
    }
    case "image":
      body = (
        <div>
          <Slot
            value={b.src} code={b.code} label={b.label}
            variant={b.size === "wide" ? "slot--wide" : b.size === "tall" ? "slot--tall" : ""}
            style={b.maxH ? { height: `${b.maxH}mm`, width: "auto", aspectRatio: "4 / 3", margin: "0 auto" } : undefined}
            scale={b.scale ?? 100} zoom={b.zoom ?? 100} panX={b.panX ?? 0} panY={b.panY ?? 0}
            onChange={(src) => set({ src })}
            onScaleChange={(scale) => set({ scale })}
            onZoomChange={(zoom) => set({ zoom })}
            onPanChange={({ panX, panY }) => set({ panX, panY })}
          />
          <EditableText tag="div" className="slot__cap" editing={editing} value={b.caption} onCommit={field("caption")} placeholder="Caption" />
          {editing && (
            <div className="block-opts">
              <label>Size <select value={b.size} onChange={(e) => set({ size: e.target.value })}>
                <option value="normal">4:3</option><option value="wide">16:9</option><option value="tall">3:4 (tall)</option>
              </select></label>
              <label>Code <input value={b.code} style={{ width: 60 }} onChange={(e) => set({ code: e.target.value })} /></label>
            </div>
          )}
        </div>
      );
      break;
    case "imagerow":
      body = (
        <div>
          <EditableText tag="p" className="imgrow__title" editing={editing} value={b.title} onCommit={field("title")} placeholder="Row title" />
          <div className="imgrow__grid" style={{ gridTemplateColumns: `repeat(${b.cols}, 1fr)` }}>
            {b.slots.map((s, idx) => (
              <div key={s.id} style={{ position: "relative" }}>
                {editing && idx > 0 && (
                  <button className="kititem__breakbtn" title="Insert page break before this slot" onClick={() => ctx.splitBlockAt(b.id, "slots", idx)}><Scissors /></button>
                )}
                <Slot
                  value={s.src} code={s.code} label={s.label}
                  variant={b.aspect === "16:9" ? "slot--wide" : ""}
                  scale={s.scale ?? 100} zoom={s.zoom ?? 100} panX={s.panX ?? 0} panY={s.panY ?? 0}
                  onChange={(src) => set((x) => ({ slots: x.slots.map((y) => y.id === s.id ? { ...y, src } : y) }))}
                  onScaleChange={(scale) => set((x) => ({ slots: x.slots.map((y) => y.id === s.id ? { ...y, scale } : y) }))}
                  onZoomChange={(zoom) => set((x) => ({ slots: x.slots.map((y) => y.id === s.id ? { ...y, zoom } : y) }))}
                  onPanChange={({ panX, panY }) => set((x) => ({ slots: x.slots.map((y) => y.id === s.id ? { ...y, panX, panY } : y) }))}
                />
                <EditableText tag="div" className="slot__cap" editing={editing} value={s.caption} onCommit={(v) => set((x) => ({ slots: x.slots.map((y) => y.id === s.id ? { ...y, caption: v } : y) }))} placeholder="Caption" />
                {editing && <div style={{ textAlign: "center", marginTop: 3 }}><button className="item-del" onClick={() => set((x) => ({ slots: x.slots.filter((y) => y.id !== s.id) }))}><X /></button></div>}
              </div>
            ))}
          </div>
          {editing && (
            <div className="block-opts">
              <label>Columns <select value={b.cols} onChange={(e) => set({ cols: Number(e.target.value) })}>
                <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
              </select></label>
              <label>Aspect <select value={b.aspect || "4:3"} onChange={(e) => set({ aspect: e.target.value })}>
                <option value="4:3">4:3</option><option value="16:9">16:9</option>
              </select></label>
              <button className="mini-btn" onClick={() => set((x) => ({ slots: [...x.slots, { id: makeId("s"), code: "•", label: "Slot", caption: "Caption", src: null, scale: 100, zoom: 100, panX: 0, panY: 0 }] }))}><Plus /> Add slot</button>
            </div>
          )}
        </div>
      );
      break;
    case "callout":
      body = (
        <div>
          <div className={`callout callout--${b.ctype}`}>
            <CalloutIcon t={b.ctype} />
            <div><b>{b.label} </b><EditableText tag="span" editing={editing} value={b.text} onCommit={field("text")} placeholder="Callout text" /></div>
          </div>
          {editing && (
            <div className="block-opts">
              <label>Type <select value={b.ctype} onChange={(e) => set({ ctype: e.target.value })}>
                <option value="tip">Tip</option><option value="info">Info</option><option value="warn">Warning</option><option value="danger">Danger</option><option value="note">Note</option>
              </select></label>
              <label>Label <input value={b.label} style={{ width: 80 }} onChange={(e) => set({ label: e.target.value })} /></label>
            </div>
          )}
        </div>
      );
      break;
    case "kitlist":
      body = (
        <div>
          <EditableText tag="p" className="kit__title" editing={editing} value={b.title} onCommit={field("title")} placeholder="List title" />
          <div className="kit__grid">
            {b.items.map((it, idx) => (
              <div key={it.id} className="kititem">
                {editing && idx > 0 && (
                  <button className="kititem__breakbtn" title="Insert page break before this item" onClick={() => ctx.splitBlockAt(b.id, "items", idx)}><Scissors /></button>
                )}
                <div className="kititem__media">
                  <Slot
                    value={it.image?.src} code={it.qty} label={it.label || "Item"} hint="Photo"
                    variant="slot--kit"
                    scale={it.image?.scale ?? 100} zoom={it.image?.zoom ?? 100} panX={it.image?.panX ?? 0} panY={it.image?.panY ?? 0}
                    onChange={(src) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, image: { ...(y.image || {}), src, scale: y.image?.scale ?? 100, zoom: y.image?.zoom ?? 100, panX: y.image?.panX ?? 0, panY: y.image?.panY ?? 0 } } : y) }))}
                    onScaleChange={(scale) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, image: { ...(y.image || {}), scale } } : y) }))}
                    onZoomChange={(zoom) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, image: { ...(y.image || {}), zoom } } : y) }))}
                    onPanChange={({ panX, panY }) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, image: { ...(y.image || {}), panX, panY } } : y) }))}
                  />
                </div>
                <div className="kititem__info">
                  <span className="qty"><EditableText tag="span" editing={editing} value={it.qty} onCommit={(v) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, qty: v } : y) }))} placeholder="1×" /></span>
                  <EditableText tag="span" className="kititem__label" editing={editing} value={it.label} onCommit={(v) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, label: v } : y) }))} placeholder="Item" style={{ flex: 1 }} />
                  {editing && <button className="item-del" onClick={() => set((x) => ({ items: x.items.filter((y) => y.id !== it.id) }))}><X /></button>}
                </div>
              </div>
            ))}
          </div>
          {editing && <div className="block-opts"><button className="mini-btn" onClick={() => set((x) => ({ items: [...x.items, { id: makeId("i"), qty: "1×", label: "Item", image: null }] }))}><Plus /> Add item</button></div>}
        </div>
      );
      break;
    case "checklist":
      body = (
        <div className="chk">
          <EditableText tag="p" className="chk__title" editing={editing} value={b.title} onCommit={field("title")} placeholder="Checklist title" />
          <ul>
            {b.items.map((it, idx) => (
              <li key={it.id}>
                <CheckCircle2 />
                <EditableText tag="span" editing={editing} value={it.label} onCommit={(v) => set((x) => ({ items: x.items.map((y) => y.id === it.id ? { ...y, label: v } : y) }))} placeholder="Item" style={{ flex: 1 }} />
                {editing && idx > 0 && <button className="item-break" title="Insert page break before this item" onClick={() => ctx.splitBlockAt(b.id, "items", idx)}><Scissors /></button>}
                {editing && <button className="item-del" onClick={() => set((x) => ({ items: x.items.filter((y) => y.id !== it.id) }))}><X /></button>}
              </li>
            ))}
          </ul>
          {editing && <div className="block-opts"><button className="mini-btn" onClick={() => set((x) => ({ items: [...x.items, { id: makeId("i"), label: "Item" }] }))}><Plus /> Add item</button></div>}
        </div>
      );
      break;
    case "features": {
      const setItem = (itemId, patch) => set((x) => ({ items: x.items.map((y) => y.id === itemId ? { ...y, ...(typeof patch === "function" ? patch(y) : patch) } : y) }));
      body = (
        <div style={{ display: "flex", flexDirection: "column", gap: "2mm" }}>
          {b.items.map((it, idx) => (
            <div key={it.id} className="feat">
              {b.numbered ? (
                <span className="feat__num">{idx + 1}</span>
              ) : <CheckCircle2 />}
              <div className="feat__body">
                <div className="feat__text">
                  <h4><EditableText tag="span" editing={editing} value={it.title} onCommit={(v) => setItem(it.id, { title: v })} placeholder="Title" /></h4>
                  {it.bulleted ? (
                    <ul className="feat__bullets">
                      {(it.bullets && it.bullets.length ? it.bullets : [""]).map((bt, bi) => (
                        <li key={bi}>
                          <EditableText tag="span" editing={editing} value={bt} onCommit={(v) => setItem(it.id, (y) => ({ bullets: (y.bullets && y.bullets.length ? y.bullets : [""]).map((z, zi) => zi === bi ? v : z) }))} placeholder="Bullet text" style={{ flex: 1 }} />
                          {editing && (it.bullets?.length || 1) > 1 && (
                            <button className="item-del" onClick={() => setItem(it.id, (y) => ({ bullets: (y.bullets || [""]).filter((_, zi) => zi !== bi) }))}><X /></button>
                          )}
                        </li>
                      ))}
                      {editing && (
                        <li className="feat__bullets-add">
                          <button className="mini-btn" onClick={() => setItem(it.id, (y) => ({ bullets: [...(y.bullets && y.bullets.length ? y.bullets : [""]), ""] }))}><Plus /> Add bullet</button>
                        </li>
                      )}
                    </ul>
                  ) : (
                    <EditableText tag="p" editing={editing} value={it.text} onCommit={(v) => setItem(it.id, { text: v })} placeholder="Description" />
                  )}
                  {editing && (
                    <div className="feat__text-opts">
                      <label><input type="checkbox" checked={!!it.bulleted} onChange={(e) => setItem(it.id, { bulleted: e.target.checked })} /> Bulleted</label>
                      {!it.image && (
                        <button className="mini-btn" onClick={() => setItem(it.id, { image: { src: null, scale: 100, zoom: 100, panX: 0, panY: 0 } })}><ImagePlus /> Add photo</button>
                      )}
                    </div>
                  )}
                </div>
                {it.image ? (
                  <div className="feat__photo">
                    <Slot
                      value={it.image.src} code="" label={it.title || "Photo"}
                      scale={it.image.scale ?? 100} zoom={it.image.zoom ?? 100} panX={it.image.panX ?? 0} panY={it.image.panY ?? 0}
                      onChange={(src) => setItem(it.id, (y) => ({ image: { ...(y.image || {}), src } }))}
                      onScaleChange={(scale) => setItem(it.id, (y) => ({ image: { ...(y.image || {}), scale } }))}
                      onZoomChange={(zoom) => setItem(it.id, (y) => ({ image: { ...(y.image || {}), zoom } }))}
                      onPanChange={({ panX, panY }) => setItem(it.id, (y) => ({ image: { ...(y.image || {}), panX, panY } }))}
                    />
                    {editing && <button className="mini-btn mini-btn--danger" onClick={() => setItem(it.id, { image: null })}><Trash2 /> Remove photo</button>}
                  </div>
                ) : null}
              </div>
              {editing && (
                <div className="feat__tools">
                  <button className="item-move" title="Move up" disabled={idx === 0} onClick={() => ctx.moveItem(b.id, "items", idx, -1)}><ArrowUp /></button>
                  <button className="item-move" title="Move down" disabled={idx === b.items.length - 1} onClick={() => ctx.moveItem(b.id, "items", idx, 1)}><ArrowDown /></button>
                  {idx > 0 && <button className="item-break" title="Insert page break before this item" onClick={() => ctx.splitBlockAt(b.id, "items", idx)}><Scissors /></button>}
                  <button className="item-del" onClick={() => set((x) => ({ items: x.items.filter((y) => y.id !== it.id) }))}><X /></button>
                </div>
              )}
            </div>
          ))}
          {editing && (
            <div className="block-opts">
              <label><input type="checkbox" checked={!!b.numbered} onChange={(e) => set({ numbered: e.target.checked })} /> Numbered</label>
              <button className="mini-btn" onClick={() => set((x) => ({ items: [...x.items, { id: makeId("i"), title: "Feature", text: "Description.", bulleted: false, bullets: null, image: null }] }))}><Plus /> Add feature</button>
            </div>
          )}
        </div>
      );
      break;
    }
    case "timebar":
      body = (
        <div className="timebar">
          <Clock />
          <div>
            <EditableText tag="b" editing={editing} value={b.bold} onCommit={field("bold")} placeholder="Headline" />
            <EditableText tag="span" editing={editing} value={b.sub} onCommit={field("sub")} placeholder="Sub text" />
          </div>
        </div>
      );
      break;
    case "support":
      body = (
        <div className="support">
          <img className="support__logo" src={ctx.logoSrc} alt={ctx.brandName} />
          <p><EditableText tag="span" editing={editing} value={b.line} onCommit={field("line")} placeholder="Line" /> <a href={`mailto:${b.email}`}><EditableText tag="span" editing={editing} value={b.email} onCommit={field("email")} placeholder="email" /></a></p>
          <EditableText tag="p" className="legal" editing={editing} value={b.legal} onCommit={field("legal")} placeholder="Legal line" />
        </div>
      );
      break;
    case "qrcode":
      body = (
        <div>
          <div className="step">
            <div className="step__body">
              <div className="step__num">
                {b.numbered && <b><EditableText tag="span" editing={editing} value={b.num} onCommit={field("num")} placeholder="#" /></b>}
                <h4><EditableText tag="span" editing={editing} value={b.title} onCommit={field("title")} placeholder="Title" /></h4>
              </div>
              <EditableText tag="p" className="step__text" editing={editing} value={b.text} onCommit={field("text")} placeholder="Description" />
            </div>
            <div className="step__media qr__media">
              <QrImage url={b.url} sizeMm={b.size ?? 28} />
              <EditableText tag="p" className="qr__url" editing={editing} value={b.url} onCommit={field("url")} placeholder="https://..." />
            </div>
          </div>
          {editing && (
            <div className="block-opts">
              <label><input type="checkbox" checked={!!b.numbered} onChange={(e) => set({ numbered: e.target.checked })} /> Numbered</label>
              <label>Size (mm) <input type="number" min="15" max="60" value={b.size ?? 28} style={{ width: 55 }} onChange={(e) => set({ size: Number(e.target.value) })} /></label>
            </div>
          )}
        </div>
      );
      break;
    default:
      body = <div className="para">Unknown block</div>;
  }

  return (
    <div className="block">
      {body}
      {editing && (
        <div className="block-tools no-print">
          <button className="ic-btn" title="Move up" onClick={() => ctx.moveBlock(b.id, -1)}><ArrowUp /></button>
          <button className="ic-btn" title="Move down" onClick={() => ctx.moveBlock(b.id, 1)}><ArrowDown /></button>
          <button className="ic-btn" title="Duplicate" onClick={() => ctx.dupBlock(b.id)}><Copy /></button>
          <button className="ic-btn" title="Insert page break after this block" onClick={() => ctx.insertPageBreakAfter(b.id)}><Scissors /></button>
          <button className="ic-btn ic-btn--danger" title="Delete" onClick={() => ctx.delBlock(b.id)}><Trash2 /></button>
        </div>
      )}
    </div>
  );
}

/* ================= page view (pure, read-only) ================= */
function PageView({ page, pageNumber, ctx }) {
  return (
    <div className="page__inner">
      {page.tag ? (
        <div className="pg-head">
          <img className="pg-head__logo" src={ctx.logoSrc} alt={ctx.brandName} />
          <div className="pg-head__tag">{page.tag}</div>
        </div>
      ) : null}
      <div className="page__blocks">
        {page.blocks.map((b) => <Block key={b.id} block={b} editing={false} ctx={ctx} />)}
      </div>
      <div className="pg-foot">
        <span>{ctx.footerText}</span>
        <span className="pg-foot__num">{pageNumber}</span>
      </div>
    </div>
  );
}

/* ================= page break marker ================= */
function PageBreakMarker({ mark, ctx }) {
  if (mark.auto) {
    return (
      <div className="pagebreak pagebreak--auto no-print">
        <span className="pagebreak__line" />
        <AlertTriangle className="pagebreak__icon" />
        <span className="pagebreak__label">Content may overflow the page here</span>
        <button className="ic-btn" title="Insert a real page break here" onClick={() => ctx.insertPageBreakBefore(mark.beforeId)}><Scissors /></button>
        <span className="pagebreak__line" />
      </div>
    );
  }
  return (
    <div className="pagebreak pagebreak--manual no-print">
      <span className="pagebreak__line" />
      <Scissors className="pagebreak__icon" />
      <input className="pagebreak__tag" value={mark.tag} placeholder="Page header label (blank = none)" onChange={(e) => ctx.setBreakTag(mark.id, e.target.value)} />
      <button className="ic-btn ic-btn--danger" title="Remove this page break" onClick={() => ctx.removePageBreak(mark.id)}><X /></button>
      <span className="pagebreak__line" />
    </div>
  );
}

/* ================= continuous edit canvas ================= */
const ADD_TYPES = ["section", "paragraph", "step", "image", "imagerow", "callout", "kitlist", "checklist", "features", "timebar", "support", "qrcode", "hero"];

function EditingCanvas({ doc, ctx, autoBreakIds, registerRef }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const effective = withAutoBreaks(doc.blocks, autoBreakIds);
  return (
    <div className="editcanvas is-editing">
      {effective.map((b) => (
        b.type === "pagebreak"
          ? <PageBreakMarker key={b.id} mark={b} ctx={ctx} />
          : (
            <div key={b.id} className="editcanvas__block" ref={(el) => registerRef(b.id, el)}>
              <Block block={b} editing={true} ctx={ctx} />
            </div>
          )
      ))}
      <div className="addblock no-print">
        <button className="addblock__btn" onClick={() => setMenuOpen((v) => !v)}><Plus /> Add block</button>
        {menuOpen && (
          <div className="addblock__menu">
            {ADD_TYPES.map((t) => (
              <button key={t} onClick={() => { ctx.addBlock(t); setMenuOpen(false); }}>{BLOCK_LABELS[t]}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= brand settings modal ================= */
function BrandSettingsModal({ brand, onChange, onClose }) {
  return (
    <div className="modal-overlay no-print" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <h3><Settings /> Brand settings</h3>
          <button className="ic-btn" onClick={onClose}><X /></button>
        </div>
        <div className="modal__body">
          <label className="modal__field">
            <span>Brand name</span>
            <input value={brand.name} onChange={(e) => onChange({ name: e.target.value })} />
          </label>
          <label className="modal__field">
            <span>Accent colour</span>
            <input type="color" value={brand.accent} onChange={(e) => onChange({ accent: e.target.value })} />
          </label>
          <label className="modal__field">
            <span>Footer text (shown on every printed page)</span>
            <input value={brand.footerText} onChange={(e) => onChange({ footerText: e.target.value })} />
          </label>
          <label className="modal__field">
            <span>Default support email</span>
            <input value={brand.supportEmail} onChange={(e) => onChange({ supportEmail: e.target.value })} />
          </label>
          <label className="modal__field">
            <span>Default legal line</span>
            <input value={brand.legalLine} onChange={(e) => onChange({ legalLine: e.target.value })} />
          </label>
          <p className="modal__hint">These become the defaults for new manuals and new Support/QR/logo blocks on this deployment. Logo image is set separately via the cover page's "Upload logo" button.</p>
        </div>
      </div>
    </div>
  );
}

/* ================= manuals library panel ================= */
function ManualsPanel({ apiCall, manualsToken, doc, manualMeta, setManualMeta, onLoadDoc, onClose, onAuthExpired, defaultName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveName, setSaveName] = useState(manualMeta.name || defaultName || "Untitled manual");
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true); setError("");
    apiCall("list")
      .then((data) => { setItems(data.items || []); })
      .catch((e) => { if (e.status === 401) onAuthExpired(); else setError(e.message); })
      .finally(() => setLoading(false));
  }, [apiCall, onAuthExpired]);

  useEffect(() => { refresh(); }, [refresh]);

  const doSave = (asNew) => {
    if (!saveName.trim()) { setError("Please enter a name."); return; }
    setLoading(true); setError("");
    apiCall("save", { method: "POST", body: { id: asNew ? null : manualMeta.id, name: saveName.trim(), doc } })
      .then((res) => { setManualMeta({ id: res.id, name: res.name }); refresh(); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  return (
    <div className="modal-overlay no-print" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <h3><FolderOpen /> Manuals library</h3>
          <button className="ic-btn" onClick={onClose}><X /></button>
        </div>
        <div className="modal__body">
              <div className="manuals__save">
                <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Manual name" />
                <button className="tn-btn" disabled={loading} onClick={() => doSave(false)} title="Update the currently loaded entry (or create one if none loaded)"><Save /> Save</button>
                <button className="tn-btn" disabled={loading} onClick={() => doSave(true)}><Copy /> Save as new</button>
              </div>
              {error && <p className="modal__error">{error}</p>}
              <div className="manuals__list">
                {loading && <div className="manuals__loading"><Loader2 className="spin" /> Loading…</div>}
                {!loading && items.length === 0 && <div className="manuals__empty">No saved manuals yet.</div>}
                {items.map((it) => (
                  <div className="manuals__item" key={it.id}>
                    <div className="manuals__item-info">
                      <b>{it.name}{it.id === manualMeta.id && <span className="manuals__item-current">Currently loaded</span>}</b>
                      <span>{it.updatedAt ? new Date(it.updatedAt).toLocaleString() : ""}</span>
                    </div>
                    <div className="manuals__item-actions">
                      <button className="mini-btn" title="Load this manual into the editor" disabled={busyId === it.id} onClick={async () => {
                        setBusyId(it.id); setError("");
                        try {
                          const base = (process.env.PUBLIC_URL || "") + "/api.php";
                          const params = new URLSearchParams({ action: "load", id: it.id, token: manualsToken });
                          const res = await fetch(`${base}?${params.toString()}`);
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error || "Load failed");
                          onLoadDoc(data.doc);
                          setManualMeta({ id: data.id, name: data.name });
                          setSaveName(data.name);
                          onClose();
                        } catch (e) { setError(e.message); }
                        setBusyId(null);
                      }}><FolderOpen /> Load</button>
                      <button className="mini-btn" title="Download this manual as a JSON file" disabled={busyId === it.id} onClick={async () => {
                        setBusyId(it.id); setError("");
                        try {
                          const base = (process.env.PUBLIC_URL || "") + "/api.php";
                          const params = new URLSearchParams({ action: "load", id: it.id, token: manualsToken });
                          const res = await fetch(`${base}?${params.toString()}`);
                          const data = await res.json();
                          if (!res.ok) throw new Error(data.error || "Download failed");
                          const blob = new Blob([JSON.stringify(data.doc, null, 2)], { type: "application/json" });
                          const url = URL.createObjectURL(blob); const a = document.createElement("a");
                          const slug = (data.name || "manual").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "manual";
                          a.href = url; a.download = `${slug}.json`; a.click(); URL.revokeObjectURL(url);
                        } catch (e) { setError(e.message); }
                        setBusyId(null);
                      }}><Download /> Download</button>
                      <button className="mini-btn mini-btn--danger" title="Delete this manual" disabled={busyId === it.id} onClick={() => {
                        if (!window.confirm(`Delete "${it.name}"? This can't be undone.`)) return;
                        setBusyId(it.id);
                        apiCall("delete", { method: "POST", body: { id: it.id } })
                          .then(() => { if (manualMeta.id === it.id) setManualMeta({ id: null, name: "" }); refresh(); })
                          .catch((e) => setError(e.message))
                          .finally(() => setBusyId(null));
                      }}><Trash2 /> Delete</button>
                    </div>
                  </div>
                ))}
              </div>
        </div>
      </div>
    </div>
  );
}

/* ================= imposition ================= */
function buildSides(pageCount) {
  const total = Math.max(4, Math.ceil(pageCount / 4) * 4);
  const S = total / 4;
  const sides = [];
  for (let s = 0; s < S; s++) {
    sides.push({ label: `Sheet ${s + 1} — Front`, left: total - 2 * s, right: 1 + 2 * s });
    sides.push({ label: `Sheet ${s + 1} — Back`, left: 2 + 2 * s, right: total - 1 - 2 * s });
  }
  return { sides, total };
}

/* ================= app ================= */
const HORIZONTAL_DEFAULT = (process.env.PUBLIC_URL || "") + "/truenewel-logo.png";
const STACKED_DEFAULT = (process.env.PUBLIC_URL || "") + "/truenewel-logo-stacked.webp";

export default function App() {
  /* Opened via the toolbar's "User guide" button, which links to the same
     app with ?guide=1 — loads the bundled walkthrough instead of your
     saved manual, and is never written back to localStorage, so it can
     never overwrite real work. */
  const [isGuideMode] = useState(() => new URLSearchParams(window.location.search).get("guide") === "1");
  const [doc, setDocRaw] = useState(() => {
    if (isGuideMode) return migrateDoc(USER_GUIDE_DOC);
    try { const s = localStorage.getItem("tn-doc-v4"); if (s) return migrateDoc(JSON.parse(s)); } catch { /* ignore */ }
    try { const legacy = localStorage.getItem("tn-doc-v3"); if (legacy) return migrateDoc(JSON.parse(legacy)); } catch { /* ignore */ }
    return defaultDoc();
  });
  /* Undo/redo history. `setDoc` is used transparently everywhere below —
     every structural mutation pushes the previous doc onto the undo stack
     and clears the redo stack, exactly like a normal editor. */
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const setDoc = useCallback((updater) => {
    setDocRaw((d) => {
      const next = typeof updater === "function" ? updater(d) : updater;
      if (next === d) return d;
      undoStackRef.current = [...undoStackRef.current, d].slice(-100);
      redoStackRef.current = [];
      setCanUndo(true); setCanRedo(false);
      return next;
    });
  }, []);
  const undo = useCallback(() => {
    setDocRaw((d) => {
      const stack = undoStackRef.current;
      if (!stack.length) return d;
      const prev = stack[stack.length - 1];
      undoStackRef.current = stack.slice(0, -1);
      redoStackRef.current = [...redoStackRef.current, d];
      setCanUndo(undoStackRef.current.length > 0); setCanRedo(true);
      return prev;
    });
  }, []);
  const redo = useCallback(() => {
    setDocRaw((d) => {
      const stack = redoStackRef.current;
      if (!stack.length) return d;
      const next = stack[stack.length - 1];
      redoStackRef.current = stack.slice(0, -1);
      undoStackRef.current = [...undoStackRef.current, d];
      setCanUndo(true); setCanRedo(redoStackRef.current.length > 0);
      return next;
    });
  }, []);
  useEffect(() => {
    const handler = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const el = document.activeElement;
      const isEditingField = el && (el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA");
      if (isEditingField) return; // let native undo work inside text fields
      if (e.key.toLowerCase() === "z" && e.shiftKey) { e.preventDefault(); redo(); }
      else if (e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if (e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);
  const [docKey, setDocKey] = useState(0);
  const [editing, setEditing] = useState(false);
  const [layout, setLayout] = useState("reading"); // reading | booklet
  const [saveErr, setSaveErr] = useState(false);
  const [autoBreakIds, setAutoBreakIds] = useState(() => new Set());
  const importRef = useRef(null);
  const blockElsRef = useRef(new Map());

  /* ---- server-side manuals library (Hostinger, via public/api.php) ---- */
  const [brandOpen, setBrandOpen] = useState(false);
  const [manualsOpen, setManualsOpen] = useState(false);
  const [manualsToken, setManualsTokenState] = useState(() => localStorage.getItem("tn-manuals-token") || "");
  const [manualMeta, setManualMeta] = useState({ id: null, name: "" }); // currently loaded/saved library entry
  const setManualsToken = useCallback((t) => {
    setManualsTokenState(t);
    try { localStorage.setItem("tn-manuals-token", t); } catch { /* ignore */ }
  }, []);
  const apiCall = useCallback(async (action, { method = "GET", body } = {}) => {
    const base = (process.env.PUBLIC_URL || "") + "/api.php";
    const params = new URLSearchParams({ action, token: manualsToken });
    const opts = { method };
    if (body) {
      opts.headers = { "Content-Type": "application/json" };
      opts.body = JSON.stringify({ ...body, token: manualsToken });
    }
    const res = await fetch(`${base}?${params.toString()}`, opts);
    let data = {};
    try { data = await res.json(); } catch { /* ignore */ }
    if (!res.ok) { const err = new Error(data.error || `Request failed (${res.status})`); err.status = res.status; throw err; }
    return data;
  }, [manualsToken]);

  /* One-click save straight from the main toolbar — updates the currently
     loaded library entry with no dialog. If nothing is loaded yet (no name
     to save under), it opens the Manuals panel instead so a name can be
     given, same as "Save as new" there. */
  const [quickSaveBusy, setQuickSaveBusy] = useState(false);
  const [quickSaveOk, setQuickSaveOk] = useState(false);
  const quickSave = useCallback(() => {
    if (!manualMeta.id) { setManualsOpen(true); return; }
    setQuickSaveBusy(true);
    apiCall("save", { method: "POST", body: { id: manualMeta.id, name: manualMeta.name, doc } })
      .then((res) => {
        setManualMeta({ id: res.id, name: res.name });
        setQuickSaveOk(true);
        setTimeout(() => setQuickSaveOk(false), 1500);
      })
      .catch((e) => alert(e.message || "Save failed"))
      .finally(() => setQuickSaveBusy(false));
  }, [apiCall, manualMeta, doc]);

  /* ---- whole-app login gate ----
     Nothing else renders until a valid shared password (checked against
     the same api.php/config.php the manuals library uses) is supplied.
     The verified password doubles as the manuals-library token, so once
     you're in, Save/Load/Delete just work with no second prompt. */
  const [unlocked, setUnlocked] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const verifyPassword = useCallback(async (pwd) => {
    const base = (process.env.PUBLIC_URL || "") + "/api.php";
    const params = new URLSearchParams({ action: "list", token: pwd });
    const res = await fetch(`${base}?${params.toString()}`);
    if (res.status === 401) return false;
    let data;
    try { data = await res.json(); } catch { throw new Error("Unexpected server response — is api.php installed and running here?"); }
    if (!res.ok) throw new Error(data.error || `Server error (${res.status})`);
    if (!Array.isArray(data.items)) throw new Error("Unexpected server response — is api.php installed and running here?");
    return true;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!manualsToken) { setCheckingAuth(false); return; }
    verifyPassword(manualsToken)
      .then((ok) => { if (cancelled) return; if (ok) setUnlocked(true); else setManualsToken(""); })
      .catch(() => { /* server unreachable right now — stay locked, let the user retry */ })
      .finally(() => { if (!cancelled) setCheckingAuth(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLogin = useCallback((e) => {
    e.preventDefault();
    setLoginBusy(true); setLoginError("");
    verifyPassword(loginPassword)
      .then((ok) => {
        if (!ok) { setLoginError("Incorrect password."); return; }
        setManualsToken(loginPassword);
        setUnlocked(true);
        setLoginPassword("");
      })
      .catch((err) => setLoginError(err.message || "Could not reach the server."))
      .finally(() => setLoginBusy(false));
  }, [loginPassword, verifyPassword, setManualsToken]);

  const lockApp = useCallback(() => {
    setManualsToken("");
    setUnlocked(false);
  }, [setManualsToken]);

  useEffect(() => {
    if (isGuideMode) return; // never persist the built-in guide over a real manual
    try { localStorage.setItem("tn-doc-v4", JSON.stringify(doc)); setSaveErr(false); }
    catch { setSaveErr(true); }
  }, [doc, isGuideMode]);

  const editMode = editing && layout === "reading";

  const registerRef = useCallback((id, el) => {
    if (el) blockElsRef.current.set(id, el); else blockElsRef.current.delete(id);
  }, []);

  /* Recompute automatic page breaks by measuring the real rendered height
     of every block in the continuous editor. Only runs while editing —
     Done mode / print / booklet keep the last computed breaks, matching
     the "switch to Done mode to see the effect" workflow. */
  useLayoutEffect(() => {
    if (!editMode) return;
    const recompute = () => {
      const next = new Set();
      let acc = 0; let first = true;
      for (const b of doc.blocks) {
        if (b.type === "pagebreak") { acc = 0; first = true; continue; }
        const el = blockElsRef.current.get(b.id);
        const h = el ? el.getBoundingClientRect().height : 0;
        const add = h + (first ? 0 : BLOCK_GAP_PX);
        if (!first && acc + add > AVAILABLE_PAGE_PX) { next.add(b.id); acc = h; }
        else { acc += add; }
        first = false;
      }
      setAutoBreakIds((prev) => (sameIdSet(prev, next) ? prev : next));
    };
    recompute();
    /* A second pass on the next animation frame catches any layout that
       settles after this commit (e.g. web fonts swapping in, images
       finishing decode) so removed/inserted breaks always reflect the
       final, settled layout rather than a possibly-stale first measure. */
    const raf = requestAnimationFrame(recompute);
    const ro = new ResizeObserver(recompute);
    blockElsRef.current.forEach((el) => el && ro.observe(el));
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [doc.blocks, doc.fonts, editMode]);

  /* Real pagination (Done view / print / booklet) is driven only by the
     manual pagebreak blocks actually stored in the document — the
     auto-detected overflow markers are editor-only hints, never silently
     inserted into the printed output. */
  const computedPages = useMemo(() => groupIntoPages(doc.blocks), [doc.blocks]);

  /* ---- mutations (flat block list) ---- */
  const mutate = useCallback((blockId, fn) => setDoc((d) => ({
    ...d, blocks: d.blocks.map((b) => b.id !== blockId ? b : fn(b)),
  })), [setDoc]);
  const moveBlock = useCallback((blockId, dir) => setDoc((d) => {
    const i = d.blocks.findIndex((b) => b.id === blockId); const j = i + dir;
    if (i < 0 || j < 0 || j >= d.blocks.length) return d;
    const bl = [...d.blocks];[bl[i], bl[j]] = [bl[j], bl[i]]; return { ...d, blocks: bl };
  }), [setDoc]);
  const dupBlock = useCallback((blockId) => setDoc((d) => {
    const i = d.blocks.findIndex((b) => b.id === blockId); if (i < 0) return d;
    const clone = reId(d.blocks[i]); const bl = [...d.blocks]; bl.splice(i + 1, 0, clone); return { ...d, blocks: bl };
  }), [setDoc]);
  const delBlock = useCallback((blockId) => setDoc((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== blockId) })), [setDoc]);
  const addBlock = useCallback((type) => setDoc((d) => ({ ...d, blocks: [...d.blocks, makeBlock(type, { ...DEFAULT_BRAND, ...(d.brand || {}) })] })), [setDoc]);
  const insertPageBreakAfter = useCallback((blockId) => setDoc((d) => {
    const i = d.blocks.findIndex((b) => b.id === blockId); if (i < 0) return d;
    const bl = [...d.blocks]; bl.splice(i + 1, 0, { id: makeId("pb"), type: "pagebreak", tag: "" }); return { ...d, blocks: bl };
  }), [setDoc]);
  const insertPageBreakBefore = useCallback((blockId) => setDoc((d) => {
    const i = d.blocks.findIndex((b) => b.id === blockId); if (i < 0) return d;
    const bl = [...d.blocks]; bl.splice(i, 0, { id: makeId("pb"), type: "pagebreak", tag: "" }); return { ...d, blocks: bl };
  }), [setDoc]);
  const removePageBreak = useCallback((breakId) => setDoc((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== breakId) })), [setDoc]);
  const setBreakTag = useCallback((breakId, tag) => setDoc((d) => ({ ...d, blocks: d.blocks.map((b) => b.id === breakId ? { ...b, tag } : b) })), [setDoc]);
  const setFonts = useCallback((patch) => setDoc((d) => ({ ...d, fonts: { ...d.fonts, ...patch } })), [setDoc]);
  /* Split an itemized block (kit list / checklist / features items, image
     row slots) into two blocks at a given item index, with a real manual
     page break inserted between them — lets a long list be divided across
     pages exactly where the user wants, rather than only between blocks. */
  const splitBlockAt = useCallback((blockId, listKey, itemIndex) => setDoc((d) => {
    const i = d.blocks.findIndex((b) => b.id === blockId); if (i < 0) return d;
    const b = d.blocks[i]; const list = b[listKey] || [];
    if (itemIndex <= 0 || itemIndex >= list.length) return d;
    const first = { ...b, [listKey]: list.slice(0, itemIndex) };
    const second = { ...b, id: makeId(b.type), [listKey]: list.slice(itemIndex) };
    const brk = { id: makeId("pb"), type: "pagebreak", tag: "" };
    const bl = [...d.blocks]; bl.splice(i, 1, first, brk, second);
    return { ...d, blocks: bl };
  }), [setDoc]);
  /* Reorder a single item within an itemized block's list (e.g. features
     items) — mirrors moveBlock but operates one level down. */
  const moveItem = useCallback((blockId, listKey, itemIndex, dir) => setDoc((d) => {
    const i = d.blocks.findIndex((b) => b.id === blockId); if (i < 0) return d;
    const b = d.blocks[i]; const list = b[listKey] || [];
    const j = itemIndex + dir;
    if (j < 0 || j >= list.length) return d;
    const nextList = [...list];
    [nextList[itemIndex], nextList[j]] = [nextList[j], nextList[itemIndex]];
    const bl = [...d.blocks]; bl[i] = { ...b, [listKey]: nextList };
    return { ...d, blocks: bl };
  }), [setDoc]);

  const brand = { ...DEFAULT_BRAND, ...(doc.brand || {}) };
  const ctx = { mutate, moveBlock, dupBlock, delBlock, addBlock, insertPageBreakAfter, insertPageBreakBefore, removePageBreak, setBreakTag, splitBlockAt, moveItem,
    logoSrc: doc.logo || HORIZONTAL_DEFAULT, coverLogoSrc: doc.logo || STACKED_DEFAULT, hasCustomLogo: !!doc.logo, setLogo: (v) => setDoc((d) => ({ ...d, logo: v })),
    brandName: brand.name, footerText: brand.footerText };

  /* ---- export / import / reset / print ---- */
  const exportDoc = () => {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    const slug = (brand.name || "manual").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "manual";
    a.href = url; a.download = `${slug}-assembly-template.json`; a.click(); URL.revokeObjectURL(url);
  };
  const importDoc = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => { try { const d = JSON.parse(r.result); if (!d.pages && !d.blocks) throw new Error("bad"); setDoc(migrateDoc(d)); setDocKey((k) => k + 1); } catch { alert("That doesn't look like a valid template file."); } };
    r.readAsText(f); e.target.value = "";
  };
  const reset = () => { if (!window.confirm("Reset to the default blank template? Your current edits and images will be replaced.")) return; setDoc(defaultDoc()); setDocKey((k) => k + 1); };
  const print = () => {
    let el = document.getElementById("tn-page-rule");
    if (!el) { el = document.createElement("style"); el.id = "tn-page-rule"; document.head.appendChild(el); }
    el.textContent = layout === "booklet" ? "@page{size:297mm 210mm;margin:0}" : "@page{size:148mm 210mm;margin:0}";
    window.print();
  };

  const { sides } = buildSides(computedPages.length);
  const pageByNum = (n) => (n >= 1 && n <= computedPages.length ? computedPages[n - 1] : null);
  const fonts = doc.fonts || {};
  const rootStyle = {
    "--font-heading": `'${fonts.heading || "Rajdhani"}'`,
    "--font-body": `'${fonts.body || "Hanken Grotesk"}'`,
    "--font-mono": `'${fonts.mono || "Space Mono"}'`,
    "--amber": brand.accent || DEFAULT_BRAND.accent,
  };

  if (checkingAuth) {
    return (
      <div className="tn-login" style={rootStyle}>
        <div className="tn-login__card"><Loader2 className="spin" /></div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="tn-login" style={rootStyle}>
        <form className="tn-login__card" onSubmit={submitLogin}>
          <Lock className="tn-login__icon" />
          <h2>{DEFAULT_BRAND.name} Manuals Tool</h2>
          <p>Enter the shared password to continue.</p>
          <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            autoFocus
            placeholder="Password"
          />
          {loginError && <p className="modal__error">{loginError}</p>}
          <button className="tn-btn tn-btn--primary" type="submit" disabled={loginBusy || !loginPassword}>
            {loginBusy ? <Loader2 className="spin" /> : <>Unlock</>}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={rootStyle}>
      <div className="tn-toolbar no-print">
        <div className="tn-toolbar__brand"><img src={doc.logo || HORIZONTAL_DEFAULT} alt={brand.name} /></div>
        <div className="seg">
          <button className={layout === "reading" ? "on" : ""} onClick={() => setLayout("reading")} data-testid="layout-reading"><FileText style={{ width: 14, height: 14, marginRight: 6, verticalAlign: "-2px" }} />A5 pages</button>
          <button className={layout === "booklet" ? "on" : ""} onClick={() => setLayout("booklet")} data-testid="layout-booklet"><BookOpen style={{ width: 14, height: 14, marginRight: 6, verticalAlign: "-2px" }} />Booklet (A4)</button>
        </div>
        <div className="font-picker">
          <label>Heading
            <select value={fonts.heading || "Rajdhani"} onChange={(e) => setFonts({ heading: e.target.value })}>
              {FONT_OPTIONS.heading.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label>Body
            <select value={fonts.body || "Hanken Grotesk"} onChange={(e) => setFonts({ body: e.target.value })}>
              {FONT_OPTIONS.body.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label>Mono
            <select value={fonts.mono || "Space Mono"} onChange={(e) => setFonts({ mono: e.target.value })}>
              {FONT_OPTIONS.mono.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
        </div>
        <div className="tn-toolbar__spacer" />
        <div className="tn-toolbar__actions">
          <button className="tn-btn" title="Undo (Ctrl+Z)" disabled={!canUndo} onClick={undo} data-testid="undo-doc"><Undo2 /></button>
          <button className="tn-btn" title="Redo (Ctrl+Shift+Z)" disabled={!canRedo} onClick={redo} data-testid="redo-doc"><Redo2 /></button>
          {layout === "reading" && (
            <button className={`tn-btn ${editing ? "tn-btn--active" : ""}`} onClick={() => setEditing((v) => !v)} data-testid="toggle-edit"><Pencil /> {editing ? "Done" : "Edit"}</button>
          )}
          <button
            className="tn-btn"
            onClick={quickSave}
            disabled={quickSaveBusy || isGuideMode}
            title={manualMeta.id ? `Save "${manualMeta.name}" to the manuals library` : "Save to the manuals library (will ask for a name)"}
            data-testid="quick-save"
          >
            {quickSaveBusy ? <Loader2 className="spin" /> : quickSaveOk ? <CheckCircle2 /> : <Save />} Save
          </button>
          <button className="tn-btn" onClick={() => setManualsOpen(true)} data-testid="open-manuals"><FolderOpen /> Manuals</button>
          <button className="tn-btn" onClick={() => setBrandOpen(true)} data-testid="open-brand"><Settings /></button>
          <button className="tn-btn" onClick={() => window.open(`${window.location.pathname}?guide=1`, "_blank")} title="Open the built-in user guide" data-testid="open-guide"><HelpCircle /> User guide</button>
          <button className="tn-btn" onClick={exportDoc} data-testid="export-doc"><FileDown /> Export</button>
          <button className="tn-btn" onClick={() => importRef.current?.click()} data-testid="import-doc"><FileUp /> Import</button>
          <input ref={importRef} type="file" accept="application/json,.json" onChange={importDoc} style={{ display: "none" }} />
          <button className="tn-btn" onClick={reset} data-testid="reset-doc"><RotateCcw /> Reset</button>
          <button className="tn-btn tn-btn--primary" onClick={print} data-testid="print-doc"><Printer /> Print / Save PDF</button>
          <button className="tn-btn" onClick={lockApp} title="Lock (require the password again)" data-testid="lock-app"><LogOut /></button>
        </div>
      </div>

      {isGuideMode && (
        <div className="tn-guide-banner no-print">
          <HelpCircle /> You're viewing the built-in <b>User Guide</b> — nothing here is saved. Close this tab to return to your own manual.
        </div>
      )}

      {brandOpen && (
        <BrandSettingsModal
          brand={brand}
          onChange={(patch) => setDoc((d) => ({ ...d, brand: { ...DEFAULT_BRAND, ...(d.brand || {}), ...patch } }))}
          onClose={() => setBrandOpen(false)}
        />
      )}
      {manualsOpen && (
        <ManualsPanel
          apiCall={apiCall}
          manualsToken={manualsToken}
          doc={doc}
          manualMeta={manualMeta}
          setManualMeta={setManualMeta}
          defaultName={brand.name}
          onLoadDoc={(d) => { setDoc(migrateDoc(d)); setDocKey((k) => k + 1); }}
          onClose={() => setManualsOpen(false)}
          onAuthExpired={() => { setManualsOpen(false); lockApp(); }}
        />
      )}

      <div className="tn-hint no-print">
        {layout === "reading"
          ? (editMode
            ? <>Continuous edit view — content flows freely with no page clipping. Use the <b>scissors</b> icon on any block to insert a manual page break; automatic breaks (dashed) are inserted for you wherever a page would otherwise overflow. Click <b>Done</b> to see the real A5 pages.</>
            : <>Design mode — A5 pages, 10mm safe margins, <b>no bleed</b>. Click <b>Edit</b> to add / duplicate / reorder blocks and manage page breaks.</>)
          : <>Booklet imposition — print <b>A4 landscape</b>, <b>double-sided (flip on SHORT edge)</b>, then <b>fold in half</b>. Pages fall in the correct reading order. Blank cells = padding to a multiple of 4.</>}
        {saveErr && <> · <b style={{ color: "#ffd7a1" }}>Note:</b> images too large to auto-save in this browser — use <b>Export</b> to keep your work.</>}
      </div>

      {layout === "reading" ? (
        editMode ? (
          <EditingCanvas doc={doc} ctx={ctx} autoBreakIds={autoBreakIds} registerRef={registerRef} key={docKey} />
        ) : (
          <div className="tn-stage layout-reading" key={docKey}>
            {computedPages.map((p, i) => (
              <div className="page" key={p.id}>
                <PageView page={p} pageNumber={i + 1} ctx={ctx} />
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="tn-stage tn-stage--booklet layout-booklet" key={`bk-${docKey}`}>
          {sides.map((side, i) => (
            <div key={i}>
              <div className="sheet-label no-print">{side.label} · A4 landscape (297 × 210mm)</div>
              <div className="sheet">
                <div className="fold-line no-print" />
                {[side.left, side.right].map((n, k) => {
                  const pg = pageByNum(n);
                  return (
                    <div className="sheet__cell" key={k}>
                      {pg ? <div className="page"><PageView page={pg} pageNumber={n} ctx={ctx} /></div>
                        : <div className="sheet__blank">Blank · p{n}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* deep clone a block with fresh ids */
function reId(block) {
  const b = JSON.parse(JSON.stringify(block));
  b.id = makeId("b");
  if (Array.isArray(b.items)) b.items = b.items.map((it) => ({ ...it, id: makeId("i") }));
  if (Array.isArray(b.slots)) b.slots = b.slots.map((s) => ({ ...s, id: makeId("s") }));
  return b;
}
